import React, { ChangeEventHandler, CSSProperties } from 'react';
import { twMerge } from 'tailwind-merge';
import { useRowTabIndex } from './row-tab-stop';

interface Props {
    handleChange: ChangeEventHandler<HTMLInputElement>;
    preferredValue: number;
    disabled?: boolean;
    /**
     * What the current value means in human terms. Without it a screen reader
     * reads the raw percentage ("0 percent"), which says nothing useful for a
     * position in an audio file.
     */
    ariaValueText?: string;
    /**
     * Names the slider for assistive tech. A range input has no text of its
     * own, so without this it announces as a bare "slider".
     */
    ariaLabel?: string;
    /**
     * Restyle the track. Merged after the defaults, so a caller can adjust
     * the look without forking the component.
     */
    className?: string;
    style?: CSSProperties;
    /** Range and step of the native input; browser defaults are 0, 100, 1. */
    min?: number;
    max?: number;
    step?: number;
}

// A native range input cannot style the part of the track before the thumb,
// so the fill is the input's own background: a two-stop gradient in the brand
// colour and a 10 % black overlay, split at the value and moved by one CSS
// variable. No thumb; the split marks the position.
const TRACK =
    'h-1 w-full appearance-none overflow-hidden rounded-full border-transparent bg-[linear-gradient(to_right,var(--color-main)_var(--progress),rgb(0_0_0/10%)_var(--progress))] enabled:cursor-pointer [&::-moz-range-thumb]:size-0 [&::-moz-range-thumb]:border-0 [&::-moz-range-track]:bg-transparent [&::-webkit-slider-thumb]:size-0 [&::-webkit-slider-thumb]:appearance-none';

const RangeSlider = ({
    handleChange,
    preferredValue,
    disabled = false,
    ariaValueText,
    ariaLabel,
    className,
    style,
    min = 0,
    max = 100,
    step,
}: Props) => {
    // In a list row that does not hold the list's Tab stop, the slider is out
    // of the Tab order.
    const rowTabIndex = useRowTabIndex();
    const progress =
        max > min
            ? Math.min(
                  100,
                  Math.max(0, ((preferredValue - min) / (max - min)) * 100),
              )
            : 0;

    return (
        <input
            value={preferredValue}
            onChange={handleChange}
            type="range"
            className={twMerge(TRACK, className)}
            style={{ '--progress': `${progress}%`, ...style } as CSSProperties}
            disabled={disabled}
            tabIndex={rowTabIndex}
            min={min}
            max={max}
            step={step}
            // No aria-valuemin/max/now or aria-orientation: the native input
            // publishes its own, and a hand-written aria-valuenow would carry the
            // raw prop where the browser has already snapped the value to the
            // step.
            aria-valuetext={ariaValueText}
            aria-label={ariaLabel}
        />
    );
};

export default RangeSlider;
